import { ArrowLeft, Eye, FileText, GitMerge, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  COTES_MONTAGE,
  METHODE_PAIEMENT_LABELS,
  mesuresVides,
  type Commande,
  type MethodePaiement,
} from '@sirajoptic/shared';
import { BadgeReste, BadgeStatutCommande, BadgeStatutFacture } from '@/components/ui/badge';
import { Button, variantesBouton } from '@/components/ui/button';
import { Card, CardHeader, StatCard } from '@/components/ui/card';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { date, dh, entier, ordonnance, telephone, tronquer } from '@/lib/format';
import { CommandeForm } from '@/features/commandes/components/CommandeForm';
import { CommandeDrawer } from '@/features/commandes/components/CommandeDrawer';
import { VenteDrawer } from '@/features/ventes/components/VenteDrawer';
import { useFicheClient } from './api/clients.api';
import { BandeauAmo } from './components/BandeauAmo';
import { FusionnerDialog } from './components/FusionnerDialog';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isPending, error, refetch } = useFicheClient(id);
  const naviguer = useNavigate();

  const [nouvelleCommande, setNouvelleCommande] = useState(false);
  const [commandeAModifier, setCommandeAModifier] = useState<Commande | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [venteDetail, setVenteDetail] = useState<string | null>(null);
  const [fusion, setFusion] = useState(false);

  if (isPending) return <ChargementBloc libelle="Chargement de la fiche client…" />;
  if (error) return <EtatErreur erreur={error} onReessayer={() => void refetch()} />;

  const { client, commandes, factures, ventes, derniere_ordonnance, dernieres_mesures, stats } =
    data;

  return (
    <>
      <PageHeader
        titre={client.nom}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{telephone(client.tel)}</span>
            {client.ice ? <span>ICE {client.ice}</span> : null}
          </span>
        }
        actions={
          <>
            <Link to="/clients" className={variantesBouton({ variant: 'ghost', size: 'sm' })}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Tous les clients
            </Link>
            <Button
              variant="outline"
              icone={<GitMerge aria-hidden className="h-4 w-4" />}
              onClick={() => setFusion(true)}
            >
              Fusionner avec…
            </Button>
            <Button
              variant="primary"
              icone={<Plus aria-hidden className="h-4 w-4" />}
              onClick={() => setNouvelleCommande(true)}
            >
              Nouvelle commande
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard libelle="Achats" valeur={entier(stats.nombre_achats)} />
          <StatCard libelle="Total dépensé" valeur={dh(stats.total_depense)} accent />
          <StatCard
            libelle="Reste dû"
            valeur={dh(stats.reste_du)}
            secondaire={stats.reste_du < 0 ? 'Trop-perçu' : undefined}
          />
          <StatCard
            libelle="Dernier achat"
            valeur={stats.dernier_achat ? date(stats.dernier_achat) : '—'}
          />
        </div>

        {/* Le délai de renouvellement, juste sous les chiffres : c'est la
            première question posée au comptoir quand un client revient. */}
        <BandeauAmo
          depart={stats.amo_depart}
          estime={stats.amo_depart_estime}
          className="mt-3"
        />

        {client.note ? (
          <Card className="mt-4">
            <CardHeader titre="Note" />
            <p className="px-4 py-3 text-sm text-ink-soft">{client.note}</p>
          </Card>
        ) : null}

        {/* Dernière ordonnance : l'écran que l'opticien regarde en premier. */}
        <Card className="mt-4">
          <CardHeader
            titre="Dernière ordonnance"
            description={
              derniere_ordonnance
                ? `Relevée le ${date(derniere_ordonnance.date_commande)}`
                : 'Aucune correction enregistrée pour ce client.'
            }
          />
          {derniere_ordonnance || dernieres_mesures ? (
            <div className="p-4">
              {derniere_ordonnance ? (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-md border border-line py-3">
                      <p className="text-xs uppercase tracking-wide text-ink-muted">OD</p>
                      <p className="mt-1 font-mono text-lg">{ordonnance(derniere_ordonnance.od)}</p>
                    </div>
                    <div className="rounded-md border border-line py-3">
                      <p className="text-xs uppercase tracking-wide text-ink-muted">OG</p>
                      <p className="mt-1 font-mono text-lg">{ordonnance(derniere_ordonnance.og)}</p>
                    </div>
                    <div className="rounded-md border border-line py-3">
                      <p className="text-xs uppercase tracking-wide text-ink-muted">ADD</p>
                      <p className="mt-1 font-mono text-lg">{derniere_ordonnance.addition ?? '—'}</p>
                    </div>
                  </div>

                  {derniere_ordonnance.prescripteur || derniere_ordonnance.date_ordonnance ? (
                    <p className="mt-2 text-xs text-ink-muted">
                      {derniere_ordonnance.prescripteur
                        ? `Prescrite par ${derniere_ordonnance.prescripteur}`
                        : 'Ordonnance'}
                      {derniere_ordonnance.date_ordonnance
                        ? ` — ${date(derniere_ordonnance.date_ordonnance)}`
                        : null}
                    </p>
                  ) : null}
                </>
              ) : null}

              {/* Les cotes viennent de la dernière commande qui en portait, pas
                  forcément de celle qui porte la correction. Seules celles du
                  visage se réutilisent telles quelles ; les cotes du calibre
                  sont là pour mémoire, pas pour être recopiées sur une autre
                  monture. */}
              {dernieres_mesures && !mesuresVides(dernieres_mesures) ? (
                <>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {COTES_MONTAGE.map((cote) => (
                      <div
                        key={cote.cle}
                        className="rounded-md border border-dashed border-line py-2 text-center"
                      >
                        <p className="text-xs uppercase tracking-wide text-ink-muted">
                          {cote.abrege}
                        </p>
                        <p className="mt-0.5 font-mono text-sm">
                          {dernieres_mesures[`${cote.cle}_od`] ?? '—'}
                          <span className="mx-1 text-ink-muted">/</span>
                          {dernieres_mesures[`${cote.cle}_og`] ?? '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-ink-muted">
                    Cotes de montage OD / OG, en millimètres — relevées le{' '}
                    {date(dernieres_mesures.date_commande)}.
                  </p>
                </>
              ) : null}
            </div>
          ) : (
            <EtatVide titre="Pas encore d'ordonnance" />
          )}
        </Card>

        {/* Historique complet : l'évolution de la correction se lit ligne à ligne. */}
        <Card className="mt-4">
          <CardHeader
            titre="Historique des commandes"
            description="De la plus récente à la plus ancienne — l'évolution de la correction se lit d'une visite à l'autre."
          />
          {commandes.length === 0 ? (
            <EtatVide
              titre="Aucune commande"
              description="Créez la première commande de ce client."
              action={
                <Button variant="primary" onClick={() => setNouvelleCommande(true)}>
                  Nouvelle commande
                </Button>
              }
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>N° de Bon</TH>
                  <TH>OD</TH>
                  <TH>OG</TH>
                  <TH>ADD</TH>
                  <TH>Verre</TH>
                  <TH>Monture</TH>
                  <TH>Lentilles</TH>
                  <TH numerique>Total</TH>
                  <TH>Reste</TH>
                  <TH>Statut</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {commandes.map((c) => (
                  <TR key={c.id} alerte={c.a_verifier}>
                    <TD className="whitespace-nowrap font-medium">{date(c.date_commande)}</TD>
                    <TD className="whitespace-nowrap font-mono text-xs text-ink-muted">
                      {c.numero_bon ?? '—'}
                    </TD>
                    <TD className="whitespace-nowrap font-mono text-xs">{ordonnance(c.od)}</TD>
                    <TD className="whitespace-nowrap font-mono text-xs">{ordonnance(c.og)}</TD>
                    <TD className="whitespace-nowrap font-mono text-xs">{c.addition ?? '—'}</TD>
                    <TD>{tronquer(c.type_verre, 16)}</TD>
                    <TD>{tronquer(c.monture, 16)}</TD>
                    <TD>{tronquer(c.lentilles, 16)}</TD>
                    <TD numerique className="font-semibold">
                      {dh(c.total)}
                    </TD>
                    <TD>
                      <BadgeReste reste={c.reste} />
                    </TD>
                    <TD>
                      <BadgeStatutCommande statut={c.statut} />
                    </TD>
                    <TD className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Voir le détail"
                        onClick={() => setDetail(c.id)}
                      >
                        <Eye aria-hidden className="h-4 w-4" />
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        {/* Les achats sans commande : un cordon, un étui, un produit d'entretien.
            La carte ne s'affiche que s'il y en a — au comptoir, la plupart des
            ventes restent volontairement anonymes. */}
        {ventes.length > 0 ? (
          <Card className="mt-4">
            <CardHeader
              titre="Achats au comptoir"
              description="Ventes réglées sur place et rattachées à cette fiche."
            />
            <Table>
              <THead>
                <TR>
                  <TH>N°</TH>
                  <TH>Date</TH>
                  <TH>Articles</TH>
                  <TH>Règlement</TH>
                  <TH numerique>Total</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {ventes.map((v) => (
                  <TR key={v.id} onClick={() => setVenteDetail(v.id)}>
                    <TD className="whitespace-nowrap font-mono text-xs font-medium">{v.numero}</TD>
                    <TD className="whitespace-nowrap text-ink-muted">{date(v.date_vente)}</TD>
                    <TD>{tronquer(v.lignes.map((l) => l.designation).join(', '), 44)}</TD>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {v.methode ? METHODE_PAIEMENT_LABELS[v.methode as MethodePaiement] : '—'}
                    </TD>
                    <TD numerique className="font-semibold">
                      {dh(v.total)}
                    </TD>
                    <TD className="text-right">
                      <Eye aria-hidden className="ml-auto h-4 w-4 text-ink-muted" />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        ) : null}

        <Card className="mt-4">
          <CardHeader titre="Factures" description="Documents officiels émis pour ce client." />
          {factures.length === 0 ? (
            <EtatVide
              titre="Aucune facture"
              description="Une facture se génère depuis le détail d'une commande."
              icone={<FileText aria-hidden className="h-6 w-6" />}
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Référence</TH>
                  <TH>Date</TH>
                  <TH numerique>Total HT</TH>
                  <TH numerique>TVA</TH>
                  <TH numerique>Total TTC</TH>
                  <TH>Statut</TH>
                </TR>
              </THead>
              <TBody>
                {factures.map((f) => (
                  <TR key={f.id} onClick={() => naviguer(`/factures/${f.id}`)}>
                    <TD className="whitespace-nowrap font-mono font-medium">{f.reference}</TD>
                    <TD className="whitespace-nowrap text-ink-muted">{date(f.date_facture)}</TD>
                    <TD numerique>{dh(f.total_ht)}</TD>
                    <TD numerique className="text-ink-muted">
                      {dh(f.total_tva)}
                    </TD>
                    <TD numerique className="font-semibold">
                      {dh(f.total_ttc)}
                    </TD>
                    <TD>
                      <BadgeStatutFacture statut={f.statut} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </PageBody>

      {/* Pré-remplie avec le client et sa dernière correction. */}
      <CommandeForm
        ouvert={nouvelleCommande || Boolean(commandeAModifier)}
        commande={commandeAModifier}
        clientInitial={
          commandeAModifier
            ? null
            : {
                client,
                derniereOrdonnance: derniere_ordonnance,
                dernieresMesures: dernieres_mesures,
              }
        }
        onFermer={() => {
          setNouvelleCommande(false);
          setCommandeAModifier(null);
        }}
      />

      <CommandeDrawer
        id={detail}
        onFermer={() => setDetail(null)}
        onModifier={(commande) => {
          setDetail(null);
          setCommandeAModifier(commande);
        }}
      />

      <VenteDrawer id={venteDetail} onFermer={() => setVenteDetail(null)} />

      <FusionnerDialog ouvert={fusion} clientConserve={client} onFermer={() => setFusion(false)} />
    </>
  );
}
